// How a plan travels to the server and comes back, without the network: the
// same functions serve the wizard (importClient.js) and the API tests.
// The server takes the plan one small chunk at a time and never builds it
// (api/src/routes/student-imports.js explains why).
export const CHUNK_OPS = 40;
const CHUNK_CHARS = 90000;
const STRUCTURES = ['grades', 'sections'];

/** The plan's operations as stored: a pupil's operation also carries its Excel row and its review line. */
export function stagedOperations(plan, rows) {
  const source = new Map(rows.map((r) => [r.id, r])), review = new Map(plan.results.filter((r) => r.opKey).map((r) => [r.opKey, r]));
  return plan.operations.map((op) => {
    if (op.table !== 'students') return op;
    const { matchValues, candidateIds, confirmNew, ...stored } = op;
    const { changes, familyChanges, ...result } = review.get(op.key);
    const { sourceCells, ...row } = source.get(op.rowIds[0]);
    return { ...stored, result, row };
  });
}

/** Operations cut into requests the server accepts: [{ position, operations }]. */
export function operationChunks(operations) {
  const chunks = [];
  let chunk = [], size = 0;
  operations.forEach((op, position) => {
    const length = JSON.stringify(op).length;
    if (chunk.length && (chunk.length >= CHUNK_OPS || size + length > CHUNK_CHARS)) { chunks.push(chunk); chunk = []; size = 0; }
    if (!chunk.length) chunk.position = position;
    chunk.push(op); size += length;
  });
  if (chunk.length) chunks.push(chunk);
  return chunks.map((operations) => ({ position: operations.position, operations: [...operations] }));
}

const outcome = (o) => ({ key: o.key, status: o.status || 'pending', entityId: o.entityId ?? null, before: o.before ?? null, after: o.after ?? null, error: o.error ?? null });

/** The run as the wizard shows it, right after its review was saved. */
export function savedRun(summary, plan, input, operations) {
  return { ...summary, input, results: plan.results, structures: operations.filter((o) => STRUCTURES.includes(o.table)), operations: operations.map(outcome) };
}

/** The same shape rebuilt from the server's journal (another browser, a reload, the history list). */
export function restoredRun(summary, journal) {
  const ordered = [...journal].sort((a, b) => a.position - b.position), plans = ordered.map((o) => o.plan);
  const families = new Map(plans.filter((p) => p.table === 'families').map((p) => [p.key, p]));
  const pupils = plans.filter((p) => p.table === 'students');
  return { ...summary,
    input: { filename: summary.filename, rows: pupils.map((p) => p.row), settings: summary.settings, structures: summary.structureSpecs || [] },
    results: pupils.map((p) => ({ ...p.result, changes: p.changes, familyChanges: families.get(p.familyKey)?.changes || [] })),
    structures: plans.filter((p) => STRUCTURES.includes(p.table)), operations: ordered.map(outcome) };
}

/** One step's answer folded into the run on screen. */
export function afterStep(run, answer) {
  const changed = new Map(answer.changed.map((c) => [c.key, c]));
  return { ...run, status: answer.status, operations: run.operations.map((o) => (changed.has(o.key) ? { ...o, ...changed.get(o.key) }
    : answer.stale && o.status === 'pending' ? { ...o, status: 'failed', error: answer.staleReason } : o)) };
}
